import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
});

// Add auth token to requests — skip if Authorization header already set (partner/attorney/CRO portals)
api.interceptors.request.use((config) => {
  if (!config.headers.Authorization) {
    const path = window.location.pathname;
    let token = null;
    if (path.startsWith('/partner/') || path === '/partner') {
      const isImpersonating = sessionStorage.getItem('impersonating') === 'true' ||
        new URLSearchParams(window.location.search).has('impersonate');
      token = (isImpersonating && sessionStorage.getItem('impersonation_partner_token'))
        || localStorage.getItem('partner_token');
    } else if (path.startsWith('/cro')) {
      token = localStorage.getItem('cro_token');
    } else if (path.startsWith('/attorney')) {
      token = localStorage.getItem('attorney_token');
    } else if (path.startsWith('/company')) {
      token = localStorage.getItem('company_token');
    } else {
      token = localStorage.getItem('auth_token');
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// ── Centralised error logging ─────────────────────────────────────────────
// In-memory dedup set: fingerprint → timestamp of last log POST.
// Errors with the same fingerprint within 30 s are silently dropped.
const _recentFingerprints = new Map();
const _DEDUP_MS = 30_000;

function _redactBody(raw) {
  if (!raw) return null;
  const SENSITIVE = ['password', 'token', 'ssn', 'dob', 'card', 'secret', 'key'];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    const out = {};
    for (const [k, v] of Object.entries(parsed)) {
      out[k] = SENSITIVE.some(s => k.toLowerCase().includes(s)) ? '[REDACTED]' : v;
    }
    return JSON.stringify(out).slice(0, 1000);
  } catch {
    return null;
  }
}

function _getPortal() {
  const p = window.location.pathname;
  if (p.startsWith('/partner')) return 'partner';
  if (p.startsWith('/attorney')) return 'attorney';
  if (p.startsWith('/cro')) return 'cro';
  if (p.startsWith('/company')) return 'company';
  if (p.startsWith('/admin')) return 'admin';
  return 'public';
}

function _getUserInfo() {
  try {
    const portal = _getPortal();
    const raw =
      portal === 'partner' ? localStorage.getItem('partner_info') :
      portal === 'admin'   ? localStorage.getItem('user') :
      null;
    if (raw) {
      const u = JSON.parse(raw);
      return { user_email: u.email || null, user_name: u.full_name || u.name || null, user_role: u.role || null };
    }
  } catch (_) {}
  return { user_email: null, user_name: null, user_role: null };
}

function _logError(error) {
  try {
    const status     = error.response?.status ?? null;
    const method     = (error.config?.method || 'UNKNOWN').toUpperCase();
    const rawUrl     = error.config?.url || '';
    // Strip query string; if it's a full URL strip the /api prefix too
    const endpoint   = (rawUrl.startsWith('http') ? rawUrl.replace(API, '') : rawUrl).split('?')[0];
    const errData    = error.response?.data;
    const errMsg     = (
      (typeof errData === 'string' ? errData : errData?.detail || errData?.message) ||
      error.message || 'Unknown error'
    ).slice(0, 500);

    // fingerprint: stable key for grouping identical errors
    const fpRaw      = `${method}:${endpoint}:${status ?? 'net'}:${errMsg.slice(0, 80)}`;
    const fingerprint = btoa(unescape(encodeURIComponent(fpRaw))).slice(0, 100);

    // 30-second dedup
    const now = Date.now();
    if (_recentFingerprints.has(fingerprint) && now - _recentFingerprints.get(fingerprint) < _DEDUP_MS) return;
    _recentFingerprints.set(fingerprint, now);
    // Prune stale entries
    for (const [fp, ts] of _recentFingerprints) { if (now - ts > _DEDUP_MS * 2) _recentFingerprints.delete(fp); }

    const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH']);
    const request_body_summary = WRITE_METHODS.has(method) ? _redactBody(error.config?.data) : null;
    const { user_email, user_name, user_role } = _getUserInfo();
    const payload = {
      fingerprint,
      portal:                _getPortal(),
      user_email,
      user_name,
      user_role,
      page_url:              window.location.pathname + window.location.search,
      http_method:           method,
      endpoint,
      status_code:           status,
      error_message:         errMsg,
      error_detail:          (errData && typeof errData === 'object') ? errData : null,
      action_label:          error.config?.actionLabel || null,
      user_agent:            navigator.userAgent || null,
      request_body_summary,
    };

    // Use fetch (not the api instance) to avoid re-triggering this interceptor
    fetch(`${BACKEND_URL}/api/admin/error-logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {});
  } catch (_) {
    // Never let the logger crash the app
  }
}

// Handle auth errors — only redirect to the correct login for each portal
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      // IMPORTANT: Never redirect if we're already on a login page — that causes
      // an infinite redirect loop when credentials are wrong (login attempt → 401 → redirect to login → repeat)
      const isLoginPage =
        path === '/admin/login' ||
        path === '/partner/login' ||
        path === '/cro/login' ||
        path === '/attorney/login' ||
        path === '/company/login';

      // Never redirect to portal login when an admin is impersonating — the 401 is
      // expected because there is no real portal token; the admin is just previewing the shell.
      // Also check the URL: on first load the hook's async validate hasn't set sessionStorage yet.
      const isImpersonating =
        sessionStorage.getItem('impersonating') === 'true' ||
        new URLSearchParams(window.location.search).has('impersonate');

      if (!isLoginPage && !isImpersonating) {
        if (path.startsWith('/partner/') || path === '/partner') {
          localStorage.removeItem('partner_token');
          localStorage.removeItem('partner_info');
          window.location.href = '/partner/login';
        } else if (path.startsWith('/cro')) {
          localStorage.removeItem('cro_token');
          window.location.href = '/cro/login';
        } else if (
          path.startsWith('/attorney') &&
          path !== '/attorney-marketplace' &&
          !path.startsWith('/attorney/signup') &&
          !path.startsWith('/attorneys/apply')
        ) {
          localStorage.removeItem('attorney_token');
          window.location.href = '/attorney/login';
        } else if (path.startsWith('/company') || path.startsWith('/marketplace')) {
          localStorage.removeItem('company_token');
          window.location.href = '/cro/login';
        } else if (path.startsWith('/admin')) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          window.location.href = '/admin/login';
        }
        // For public pages, don't redirect at all
      }
    }

    // 403 login-hours MID-SESSION sign-out (session_ended): clear local state and redirect. The
    // login-TIME block (no session_ended) is shown by the login form itself — don't double-handle.
    if (
      error.response?.status === 403 &&
      error.response?.data?.detail?.error === 'login_hours' &&
      error.response?.data?.detail?.session_ended === true
    ) {
      const path = window.location.pathname;
      const isLoginPage =
        path === '/admin/login' ||
        path === '/partner/login' ||
        path === '/cro/login' ||
        path === '/attorney/login' ||
        path === '/company/login';
      toast.error(error.response.data.detail.message || 'Your login window has ended — you have been signed out.');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      if (!isLoginPage) {
        window.location.href = '/admin/login';
      }
    }

    // Log all non-401 errors centrally; also skip auth/refresh and the log endpoint itself
    const _url = error.config?.url || '';
    if (
      error.response?.status !== 401 &&
      !_url.includes('/auth/refresh') &&
      !_url.includes('/error-logs')
    ) {
      _logError(error);
    }

    return Promise.reject(error);
  }
);

// Activity-based token refresh — keeps active users logged in
// Tracks real user interactions (typing, clicking, scrolling, pasting)
// Only refreshes token if user has been active in the last 20 minutes
// If truly idle for 30+ minutes, token expires and user gets logged out
let _refreshInterval = null;
let _lastActivity = Date.now();

function trackActivity() {
  _lastActivity = Date.now();
}

function startTokenRefresh() {
  if (_refreshInterval) return;

  // Track real user activity
  const events = ['keydown', 'mousedown', 'scroll', 'touchstart', 'paste', 'input', 'click'];
  events.forEach(evt => document.addEventListener(evt, trackActivity, { passive: true }));

  _refreshInterval = setInterval(async () => {
    const path = window.location.pathname;
    if (!path.startsWith('/admin')) return;
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    // Only refresh if user was active in the last 20 minutes
    const idleMs = Date.now() - _lastActivity;
    if (idleMs > 20 * 60 * 1000) return; // Truly idle — let token expire naturally

    try {
      const res = await api.post('/auth/refresh');
      if (res.data?.access_token) {
        localStorage.setItem('auth_token', res.data.access_token);
      }
    } catch (e) {
      // Token already expired or invalid — let the 401 handler deal with it
    }
  }, 15 * 60 * 1000); // Check every 15 minutes
}

// Start refresh cycle on load if on admin page
if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
  startTokenRefresh();
}

export default api;

// Auth APIs
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getCurrentUser: () => api.get('/auth/me'),
  initSystem: () => api.post('/init'),
};

// User APIs
export const userAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Page APIs
export const pageAPI = {
  getAll: (params) => api.get('/pages', { params }),
  getById: (id) => api.get(`/pages/${id}`),
  getBySlug: (slug) => api.get(`/pages/by-slug/${slug}`),
  create: (data) => api.post('/pages', data),
  update: (id, data) => api.put(`/pages/${id}`, data),
  delete: (id) => api.delete(`/pages/${id}`),
};

// Author APIs
export const authorAPI = {
  getAll: () => api.get('/authors'),
  getById: (id) => api.get(`/authors/${id}`),
  create: (data) => api.post('/authors', data),
};

// Blog APIs
export const blogAPI = {
  getAll: (params) => api.get('/blog', { params }),
  getById: (id) => api.get(`/blog/${id}`),
  getBySlug: (slug) => api.get(`/blog/by-slug/${slug}`),
  create: (data) => api.post('/blog', data),
  update: (id, data) => api.put(`/blog/${id}`, data),
};

// Media APIs
export const mediaAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAll: () => api.get('/media'),
};

// Banner APIs
export const bannerAPI = {
  getAll: (activeOnly = false) => api.get('/banners', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/banners', data),
};

// Settings APIs
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Complaint APIs
export const complaintAPI = {
  submit: (data) => api.post('/complaints', data),
  getAll: () => api.get('/complaints'),
  getById: (id) => api.get(`/complaints/${id}`),
  updateStatus: (id, status) => api.put(`/complaints/${id}/status?status=${status}`),
};

// Review APIs
export const reviewAPI = {
  create: (data) => api.post('/reviews', data),
  getAll: (params) => api.get('/reviews', { params }),
  getById: (id) => api.get(`/reviews/${id}`),
  update: (id, data) => api.put(`/reviews/${id}`, data),
  delete: (id) => api.delete(`/reviews/${id}`),
};
